import { useContext, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { getApprovalKeyword } from '@atom/components/common/requests/customTenantUtilities';
import { Button, Progress, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import { AssetRequestStatus } from '@atom/types/assetRequest';
import api from '@atom/utilities/api';
import { INVENTORY_ASSETS_ENDPOINT } from '@atom/utilities/endpoints';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import ViewRequestContext from '../ViewRequestContext';

import classes from './sddotView.module.css';

const styles = {
  label: {
    color: colors.neutral.dim,
  },
  cancelButton: {
    marginRight: '1rem',
  },
};

const ApprovalForm = () => {
  const {
    fromAsset,
    activeRequest,
    quantityApproved,
    setQuantityApproved,
    comments,
    setComments,
    status,
    setStatus,
    setConfirmOpen,
  } = useContext(ViewRequestContext);

  // TODO: [AM-16707]: Temp code until BE solution is built
  const [fromTotalRemaining, setFromTotalRemaining] = useState<number>(0);
  const [fromTotalRemainingLoading, setFromTotalRemainingLoading] =
    useState<boolean>(false);

  const getFromTotalRemaining = async () => {
    setFromTotalRemainingLoading(true);
    const { data } = await api.get(
      `${INVENTORY_ASSETS_ENDPOINT}/${fromAsset.id}/usageSummary`,
    );

    setFromTotalRemaining(R.pathOr(0, ['totalRemaining'], data));
    setFromTotalRemainingLoading(false);
  };

  useEffect(() => {
    getFromTotalRemaining();
  }, [fromAsset]);

  const resetForm = () => {
    setQuantityApproved(null);
    setComments(null);
    setStatus(null);
  };

  const quantityUpdate = (value: any) => {
    const updatedValue =
      Math.abs(Number(value)) > 0 ? Math.abs(Number(value)) : null;

    if (updatedValue >= activeRequest?.quantityOrdered) {
      setStatus(AssetRequestStatus.FULL_APPROVAL);
    } else {
      setStatus(AssetRequestStatus.PARTIAL_APPROVAL);
    }

    setQuantityApproved(updatedValue);
  };

  const isApproval =
    status === AssetRequestStatus.FULL_APPROVAL ||
    status === AssetRequestStatus.PARTIAL_APPROVAL;

  // const availableStockError =
  //   Number(fromAsset?.budget?.quantityOnHand) - Number(quantityApproved) < 0;
  const availableStockError =
    Number(fromTotalRemaining) - Number(quantityApproved) < 0;
  const requestedQuantityError =
    Number(activeRequest?.quantityOrdered) - Number(quantityApproved) < 0;

  const quantityHelperText = useMemo(() => {
    switch (true) {
      case availableStockError:
        // return `${fromAsset?.budget?.quantityOnHand} ${fromAsset?.budget?.unit} in stock. Exceeds available stock levels.`;
        return `${fromTotalRemaining} ${fromAsset?.budget?.unit} in stock. Exceeds available stock levels.`;
      case requestedQuantityError:
        return 'Cannot approve more than the quantity requested';
      default:
        // return `${fromAsset?.budget?.quantityOnHand} ${fromAsset?.budget?.unit} in stock`;
        return `${fromTotalRemaining} ${fromAsset?.budget?.unit} in stock`;
    }
  }, [quantityApproved]);

  const approvalKeyword = getApprovalKeyword(status);
  const cancelText =
    status === AssetRequestStatus.CANCELED ? 'Do Not Cancel' : 'Cancel';
  const confirmText =
    status === AssetRequestStatus.CANCELED ? 'Cancel Request' : approvalKeyword;
  const buttonColor = isApproval ? 'primary' : 'error';
  const disableConfirm =
    isApproval &&
    (isNilOrEmpty(quantityApproved) ||
      availableStockError ||
      requestedQuantityError);

  return (
    <div className={classes.approvalForm}>
      <div className={classes.formTitle}>{`${approvalKeyword} Request`}</div>
      {isApproval && (
        <div className={clsx(classes.inputRow, classes.quantity)}>
          <TextField
            slotProps={{ inputLabel: { style: styles.label } }}
            type="number"
            value={quantityApproved || ''}
            label={`Approved Quantity (${activeRequest?.assetUnit || ''})`}
            onChange={event => quantityUpdate(event.target.value)}
            // helperText={quantityHelperText}
            helperText={
              fromTotalRemainingLoading ? (
                <Progress size={10} />
              ) : (
                quantityHelperText
              )
            }
            error={requestedQuantityError || availableStockError}
          />
        </div>
      )}
      <div className={classes.inputRow}>
        <TextField
          slotProps={{ inputLabel: { style: styles.label } }}
          fullWidth
          value={comments}
          label="Comments (optional)"
          onChange={event => setComments(event.target.value)}
        />
      </div>
      <div className={classes.approveButtons}>
        <Button sx={styles.cancelButton} onClick={resetForm}>
          {cancelText}
        </Button>
        <Button
          color={buttonColor}
          variant="contained"
          onClick={() => setConfirmOpen(true)}
          disabled={disableConfirm}
        >
          {confirmText}
        </Button>
      </div>
    </div>
  );
};

export default ApprovalForm;
